import dayjs from 'dayjs'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { confirmDialog } from 'primereact/confirmdialog'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Panel } from 'primereact/panel'
import { useId, useRef, useState } from 'react'
import { DrawerWrapper } from '~/components/common/DrawerWrapper'
import { useLanguage } from '~/contexts/LanguageContext'
import {
  useControlDocumentLinkMutation,
  useControlDocumentRemoveLinkMutation,
  useControlList,
} from '~/hooks/controlsApi'
import { useDepartmentsList } from '~/hooks/departmentsApi'
import {
  useArchiveDocument,
  useDocumentDetail,
  useDocumentFilesList,
  useReassignPic,
  useUpdateDepartment,
  useUpdateDocNoTemplate,
  useUpdateDocTitle,
  useUpdateStagingStatus,
  useUploadDocumentFile,
} from '~/hooks/documentsApi'
import useToast from '~/hooks/toast'
import { useAvailableStagingStatuses } from '~/hooks/useAvailableStagingStatuses'
import { useUsersList } from '~/hooks/usersApi'
import { useUserStore } from '~/stores/userStore'
import type { ControlItem } from '~/types/controls.types'
import { type Document, DocumentLiveStatus, DocumentStagingStatus } from '~/types/document.types'
import { UserRole } from '~/types/user.types'
import { CommentsDrawerContent } from './CommentsDrawer'
import { DocumentOfficiationDialog } from './DocumentOfficiationDialog'

export type DocumentDetailsDrawerProps = {
  documentId: number | null
  onHide: () => void
  onBack?: () => void
}

export const DocumentDetailsDrawerContent = ({
  documentId,
  onBack,
  onHide,
}: {
  documentId: number
  onBack?: () => void
  onHide?: () => void
}) => {
  const userStore = useUserStore()
  const toast = useToast()
  const { data: doc, isLoading } = useDocumentDetail(documentId)
  const usersQuery = useUsersList()

  const reassignPicMutation = useReassignPic(documentId)
  const updateTemplateMutation = useUpdateDocNoTemplate(documentId)
  const updateTitleMutation = useUpdateDocTitle(documentId)
  const updateStatusMutation = useUpdateStagingStatus(documentId)
  const updateDepartmentMutation = useUpdateDepartment(documentId)
  const uploadFileMutation = useUploadDocumentFile(documentId)

  const [isEditingTemplate, setIsEditingTemplate] = useState(false)
  const [tempTemplate, setTempTemplate] = useState('')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [tempTitle, setTempTitle] = useState('')
  const [tempTitleId, setTempTitleId] = useState('')
  const [isOfficiateDialogOpen, setIsOfficiateDialogOpen] = useState(false)
  const [isPanelExpanded, setIsPanelExpanded] = useState(false)
  const [commentControl, setCommentControl] = useState<ControlItem | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const docNumberId = useId()
  const picId = useId()
  const statusId = useId()
  const uploadId = useId()
  const departmentId = useId()
  const controlSearchId = useId()
  const titleInputId = useId()
  const titleIdInputId = useId()

  const [isLinkingControl, setIsLinkingControl] = useState(false)
  const [controlSearch, setControlSearch] = useState('')

  const { data: filesHistory, refetch: refetchFiles } = useDocumentFilesList(
    documentId,
    isPanelExpanded,
  )

  const stagingStatusOptions = useAvailableStagingStatuses(doc?.stagingStatus)

  const { lang } = useLanguage()

  const { data: departments } = useDepartmentsList()
  const { arrayData: controls } = useControlList()
  const linkControlMutation = useControlDocumentLinkMutation()
  const removeControlMutation = useControlDocumentRemoveLinkMutation()

  if (isLoading) return <div>Loading...</div>
  if (!doc) return <div>Document not found.</div>

  const currentUser = userStore.user
  const isGlobalAdmin = (currentUser?.role ?? 0) >= UserRole.Admin
  const isOfficiator = currentUser?.role === UserRole.Officiator
  const isPic = currentUser?.id === doc.picUserId

  const isDeptAdmin = currentUser?.departments?.some(
    (d) => d.departmentId === doc.departmentId && d.role === 2,
  )
  const canReassignPic = isGlobalAdmin || isDeptAdmin
  const canEditTemplate = isGlobalAdmin && doc.liveStatus === DocumentLiveStatus.Staging
  const canEditTitle = (isGlobalAdmin || isPic) && doc.liveStatus === DocumentLiveStatus.Staging
  const canUpload = isPic || isGlobalAdmin
  const canOfficiate = isOfficiator && doc.stagingStatus === DocumentStagingStatus.Final
  const canUpdateStatus = isPic || isGlobalAdmin || isDeptAdmin

  const deptMembers = usersQuery.data?.filter((u) =>
    u.departments?.some((d: { departmentId: number }) => d.departmentId === doc.departmentId),
  )

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadFileMutation.mutate(file, {
        onSuccess: () => {
          toast?.current?.show({ severity: 'success', summary: 'File uploaded successfully' })
        },
      })
    }
  }

  if (commentControl) {
    return (
      <CommentsDrawerContent
        document={doc}
        control={commentControl}
        onBack={() => setCommentControl(null)}
      />
    )
  }

  return (
    <div className='flex flex-col h-full bg-white p-4 overflow-hidden'>
      {/* Header */}
      <div className='flex items-center justify-between mb-4 shrink-0 border-b pb-2'>
        <div className='flex items-center gap-2'>
          {onBack && (
            <Button icon='pi pi-arrow-left' link onClick={onBack} className='p-0 h-8 w-8' />
          )}
          <h2 className='text-xl font-bold truncate max-w-[400px]'>
            {lang === 'id' && doc.titleId ? doc.titleId : doc.title}
          </h2>
          {!isEditingTitle && canEditTitle && (
            <Button
              icon='pi pi-pencil'
              link
              className='p-0 h-6 w-6'
              onClick={() => {
                setTempTitle(doc.title)
                setTempTitleId(doc.titleId || '')
                setIsEditingTitle(true)
              }}
            />
          )}
        </div>
        <Button icon='pi pi-times' link onClick={onHide} className='p-0 h-8 w-8 text-gray-400' />
      </div>

      {isEditingTitle && (
        <div className='mb-4 p-3 border rounded bg-blue-50 flex flex-col gap-3 shrink-0'>
          <div className='flex flex-col gap-1'>
            <label htmlFor={titleInputId} className='text-xs font-semibold text-gray-500 uppercase'>
              Title (EN)
            </label>
            <InputText
              id={titleInputId}
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              className='p-inputtext-sm'
              placeholder='English Title'
            />
          </div>
          <div className='flex flex-col gap-1'>
            <label
              htmlFor={titleIdInputId}
              className='text-xs font-semibold text-gray-500 uppercase'
            >
              Title (ID)
            </label>
            <InputText
              id={titleIdInputId}
              value={tempTitleId}
              onChange={(e) => setTempTitleId(e.target.value)}
              className='p-inputtext-sm'
              placeholder='Indonesian Title'
            />
          </div>
          <div className='flex gap-2 justify-end mt-1'>
            <Button
              label='Cancel'
              icon='pi pi-times'
              className='p-button-sm p-button-text'
              onClick={() => setIsEditingTitle(false)}
            />
            <Button
              label='Save'
              icon='pi pi-check'
              className='p-button-sm'
              loading={updateTitleMutation.isLoading}
              onClick={() => {
                updateTitleMutation.mutate(
                  { title: tempTitle, titleId: tempTitleId },
                  {
                    onSuccess: () => {
                      setIsEditingTitle(false)
                      toast?.current?.show({
                        severity: 'success',
                        summary: 'Title updated successfully',
                      })
                    },
                  },
                )
              }}
            />
          </div>
        </div>
      )}

      <div className='grow overflow-y-auto pb-20'>
        {/* Metadata Section */}
        <div className='p-4 border rounded-lg bg-gray-50 flex flex-col gap-3'>
          <h3 className='font-bold text-gray-700 uppercase text-xs tracking-wider'>Metadata</h3>

          <div className='flex flex-col gap-1'>
            {isEditingTemplate ? (
              <label htmlFor={docNumberId} className='text-xs font-semibold text-gray-500'>
                Document Number
              </label>
            ) : (
              <span className='text-xs font-semibold text-gray-500'>Document Number</span>
            )}
            <div className='flex items-center gap-2'>
              {isEditingTemplate ? (
                <div className='flex gap-1 w-full'>
                  <InputText
                    id={docNumberId}
                    value={tempTemplate}
                    onChange={(e) => setTempTemplate(e.target.value)}
                    className='h-8 text-sm grow font-mono'
                  />
                  <Button
                    icon='pi pi-check'
                    className='h-8 w-8'
                    onClick={() => {
                      updateTemplateMutation.mutate(tempTemplate, {
                        onSuccess: () => setIsEditingTemplate(false),
                      })
                    }}
                    loading={updateTemplateMutation.isLoading}
                  />
                  <Button
                    icon='pi pi-times'
                    className='h-8 w-8'
                    severity='secondary'
                    onClick={() => setIsEditingTemplate(false)}
                  />
                </div>
              ) : (
                <>
                  <span className='font-mono font-bold text-blue-700'>
                    {doc.liveDocNumber || doc.docNumberTemplate || '-'}
                  </span>
                  {canEditTemplate && (
                    <Button
                      icon='pi pi-pencil'
                      link
                      className='p-0 h-4 w-4'
                      onClick={() => {
                        setTempTemplate(doc.docNumberTemplate || '')
                        setIsEditingTemplate(true)
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            {canReassignPic ? (
              <label htmlFor={picId} className='text-xs font-semibold text-gray-500'>
                Person In Charge (PIC)
              </label>
            ) : (
              <span className='text-xs font-semibold text-gray-500'>Person In Charge (PIC)</span>
            )}
            {canReassignPic ? (
              <Dropdown
                id={picId}
                value={doc.picUserId}
                options={deptMembers}
                optionLabel='name'
                optionValue='id'
                onChange={(e) => reassignPicMutation.mutate(e.value)}
                placeholder='Select PIC'
                className='h-9 text-sm'
                loading={reassignPicMutation.isLoading}
              />
            ) : (
              <span className='font-semibold'>
                {usersQuery.data?.find((u) => u.id === doc.picUserId)?.name ||
                  `ID: ${doc.picUserId}`}
              </span>
            )}
          </div>

          <div className='flex flex-col gap-1'>
            {canUpdateStatus && stagingStatusOptions.length > 1 ? (
              <label htmlFor={statusId} className='text-xs font-semibold text-gray-500'>
                Staging Status
              </label>
            ) : (
              <span className='text-xs font-semibold text-gray-500'>Staging Status</span>
            )}
            {canUpdateStatus && stagingStatusOptions.length > 1 ? (
              <Dropdown
                id={statusId}
                value={doc.stagingStatus}
                options={stagingStatusOptions}
                onChange={(e) => updateStatusMutation.mutate(e.value)}
                className='h-9 text-sm'
                loading={updateStatusMutation.isLoading}
              />
            ) : (
              <span className='font-semibold'>
                {stagingStatusOptions.find((o) => o.value === doc.stagingStatus)?.label ??
                  doc.stagingStatus}
              </span>
            )}
          </div>
          <div className='flex flex-col gap-1'>
            {isGlobalAdmin ? (
              <label htmlFor={departmentId} className='text-xs font-semibold text-gray-500'>
                Department
              </label>
            ) : (
              <span className='text-xs font-semibold text-gray-500'>Department</span>
            )}
            {isGlobalAdmin ? (
              <Dropdown
                id={departmentId}
                value={doc.departmentId}
                options={departments}
                optionLabel='title'
                optionValue='id'
                onChange={(e) => updateDepartmentMutation.mutate(e.value)}
                placeholder='Select Department'
                className='h-9 text-sm'
                loading={updateDepartmentMutation.isLoading}
              />
            ) : (
              <span className='font-semibold'>
                {departments?.find((d) => d.id === doc.departmentId)?.title ||
                  `ID: ${doc.departmentId}`}
              </span>
            )}
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div className='flex flex-col gap-1'>
              <span className='text-xs font-semibold text-gray-500'>Created</span>
              {dayjs(doc.createTs).format('YYYY-MM-DD HH:mm:ss')}
            </div>
            <div className='flex flex-col gap-1'>
              <span className='text-xs font-semibold text-gray-500'>Updated</span>
              {dayjs(doc.updateTs).format('YYYY-MM-DD HH:mm:ss')}
            </div>
          </div>
        </div>

        {/* Current Files Section */}
        <div className='p-4 border rounded-lg flex flex-col gap-3'>
          <h3 className='font-bold text-gray-700 uppercase text-xs tracking-wider'>Files</h3>
          <div className='flex justify-between items-center'>
            <span className='text-sm'>Staging Draft:</span>
            {doc.stagingFileId ? (
              <a
                href={`/api/documents/${doc.id}/files/${doc.stagingFileId}/download`}
                target='_blank'
                rel='noreferrer'
                className='text-blue-600 font-bold flex items-center gap-1 hover:underline'
              >
                <i className='pi pi-download' /> Download
              </a>
            ) : (
              <span className='text-gray-400 italic'>No draft uploaded</span>
            )}
          </div>
          <div className='flex justify-between items-center'>
            <span className='text-sm'>Live Version:</span>
            {doc.liveFileId ? (
              <a
                href={`/api/documents/${doc.id}/files/${doc.liveFileId}/download`}
                target='_blank'
                rel='noreferrer'
                className='text-green-600 font-bold flex items-center gap-1 hover:underline'
              >
                <i className='pi pi-download' /> Download
              </a>
            ) : (
              <span className='text-gray-400 italic'>Not officiated yet</span>
            )}
          </div>

          <Panel
            header='Version History'
            toggleable
            collapsed={!isPanelExpanded}
            onToggle={() => {
              setIsPanelExpanded(!isPanelExpanded)
              if (isPanelExpanded) refetchFiles()
            }}
            className='mt-2 text-sm'
            pt={{ content: { className: 'p-0' }, header: { className: 'py-1 pr-0 pl-2' } }}
          >
            <DataTable
              value={filesHistory}
              size='small'
              paginator
              rows={5}
              emptyMessage='No history found'
              className='text-sm'
              pt={{ bodyRow: { className: 'cursor-pointer' } }}
              onRowClick={(e) => {
                window.open(`/api/documents/${doc.id}/files/${e.data.id}/download`, '_blank')
              }}
            >
              <Column
                header='Timestamp'
                body={(rowData) => dayjs(rowData.uploadTs).format('YYYY-MM-DD HH:mm:ss')}
              />
              <Column
                header='Uploader'
                body={(rowData) => {
                  const user = usersQuery.data?.find((u) => u.id === rowData.uploaderId)
                  return user ? `${user.name} (${user.email})` : `ID: ${rowData.uploaderId}`
                }}
              />
              <Column header='Staging' field='version' />
              <Column
                header='Live'
                body={(rowData) => rowData.revision?.toString().padStart(3, '0') || '-'}
              />
              <Column header='Number' field='docNumberSnapshot' />
            </DataTable>
          </Panel>
        </div>

        {/* Workflow Actions Section */}
        <div className='p-4 border rounded-lg border-blue-200 bg-blue-50 flex flex-col gap-4'>
          <h3 className='font-bold text-gray-700 uppercase text-xs tracking-wider'>
            Workflow Actions
          </h3>

          {canUpload && (
            <div className='flex flex-col gap-2'>
              <label htmlFor={uploadId} className='text-sm font-semibold'>
                Upload New Draft
              </label>
              <input
                id={uploadId}
                type='file'
                ref={fileInputRef}
                className='hidden'
                onChange={handleFileUpload}
              />
              <Button
                label='Choose File & Upload'
                icon='pi pi-upload'
                onClick={() => fileInputRef.current?.click()}
                loading={uploadFileMutation.isLoading}
                className='p-button-outlined p-button-sm'
              />
            </div>
          )}

          {canOfficiate && (
            <div className='flex flex-col gap-2 mt-2'>
              <span className='text-sm font-semibold text-blue-800'>Officiate Document</span>
              <Button
                label='Start Officiation Process'
                icon='pi pi-verified'
                severity='success'
                onClick={() => setIsOfficiateDialogOpen(true)}
              />
            </div>
          )}

          {!canUpload && !canOfficiate && (
            <p className='text-sm text-gray-500 italic text-center'>
              No workflow actions available for your role.
            </p>
          )}
        </div>

        {/* Controls Section */}
        <div className='p-4 border rounded-lg flex flex-col gap-3'>
          <div className='flex justify-between items-center'>
            <h3 className='font-bold text-gray-700 uppercase text-xs tracking-wider'>Controls</h3>
            {isGlobalAdmin && (
              <Button
                icon={isLinkingControl ? 'pi pi-times' : 'pi pi-link'}
                label={isLinkingControl ? 'Cancel' : 'Link Control'}
                className='p-button-sm p-button-text'
                onClick={() => setIsLinkingControl(!isLinkingControl)}
              />
            )}
          </div>

          {isLinkingControl && (
            <div className='flex flex-col gap-2 p-2 bg-blue-50 rounded border border-blue-100'>
              <div className='p-inputgroup h-8'>
                <span className='p-inputgroup-addon'>
                  <i className='pi pi-search' />
                </span>
                <InputText
                  id={controlSearchId}
                  value={controlSearch}
                  onChange={(e) => setControlSearch(e.target.value)}
                  placeholder='Search controls...'
                  className='text-sm'
                />
              </div>
              <div className='max-h-40 overflow-y-auto border rounded bg-white'>
                {controls
                  ?.filter(
                    (c) =>
                      (c.title.toLowerCase().includes(controlSearch.toLowerCase()) ||
                        c.code.toLowerCase().includes(controlSearch.toLowerCase())) &&
                      !doc.controlDocumentMap?.some((m) => m.controlId === c.id) &&
                      !controls.some((other) => other.parentId === c.id),
                  )
                  .map((c) => (
                    <button
                      type='button'
                      key={c.id}
                      className='p-2 hover:bg-gray-100 cursor-pointer text-sm flex justify-between items-center w-full text-left'
                      onClick={() => {
                        linkControlMutation.mutate(
                          { controlId: c.id, documentId: doc.id },
                          {
                            onSuccess: () => {
                              setIsLinkingControl(false)
                              setControlSearch('')
                              toast?.current?.show({
                                severity: 'success',
                                summary: 'Control linked successfully',
                              })
                            },
                          },
                        )
                      }}
                    >
                      <span className='font-mono mr-2'>{c.code}</span>
                      <span className='grow'>
                        {lang === 'id' && c.titleId ? c.titleId : c.title}
                      </span>
                      <i className='pi pi-plus text-blue-500' />
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div className='flex flex-col gap-2'>
            {doc.controlDocumentMap?.length ? (
              doc.controlDocumentMap.map((m) => {
                const control = controls?.find((c) => c.id === m.controlId)
                return (
                  <div
                    key={m.controlId}
                    className='flex justify-between items-center p-2 border rounded bg-white text-sm'
                  >
                    <div className='flex flex-col'>
                      <span className='font-mono font-bold text-xs text-blue-800'>
                        {control?.code ?? '...'}
                      </span>
                      <span>
                        {control
                          ? lang === 'id' && control.titleId
                            ? control.titleId
                            : control.title
                          : 'Loading...'}
                      </span>
                    </div>
                    <div className='flex items-center gap-1'>
                      <Button
                        icon='pi pi-comments'
                        rounded
                        text
                        className='h-8 w-8'
                        onClick={() => setCommentControl(control || null)}
                      />
                      {isGlobalAdmin && (
                        <Button
                          icon='pi pi-trash'
                          className='p-button-danger p-button-text p-button-sm h-8 w-8'
                          onClick={() => {
                            removeControlMutation.mutate(
                              { controlId: m.controlId, documentId: doc.id },
                              {
                                onSuccess: () => {
                                  toast?.current?.show({
                                    severity: 'success',
                                    summary: 'Control unlinked successfully',
                                  })
                                },
                              },
                            )
                          }}
                          loading={removeControlMutation.isLoading}
                        />
                      )}
                    </div>
                  </div>
                )
              })
            ) : (
              <p className='text-sm text-gray-500 italic text-center'>No controls linked.</p>
            )}
          </div>
        </div>
        <ArchiveSection document={doc} />
      </div>

      <DocumentOfficiationDialog
        document={doc}
        visible={isOfficiateDialogOpen}
        onHide={() => setIsOfficiateDialogOpen(false)}
      />
    </div>
  )
}

const ArchiveSection = ({ document: doc }: { document: Document }) => {
  const userStore = useUserStore()
  const toast = useToast()
  const archiveMutation = useArchiveDocument(doc.id)
  const currentUser = userStore.user

  const isGlobalAdmin = (currentUser?.role ?? 0) >= UserRole.Admin
  const isOfficiator = (currentUser?.role ?? 0) >= UserRole.Officiator
  const isPic = currentUser?.id === doc.picUserId
  const isDeptAdmin =
    currentUser?.departments?.some((d) => d.departmentId === doc.departmentId && d.role === 2) ??
    false

  let canArchive = false
  if (doc.liveStatus === DocumentLiveStatus.Live) {
    canArchive = isOfficiator
  } else if (doc.liveStatus === DocumentLiveStatus.Staging) {
    canArchive = isGlobalAdmin || isPic || isDeptAdmin
  }

  if (!canArchive || doc.liveStatus === DocumentLiveStatus.Archived) return null

  const confirmArchive = () => {
    confirmDialog({
      message: `Are you sure you want to archive this ${
        doc.liveStatus === DocumentLiveStatus.Live ? 'LIVE' : 'STAGING'
      } document? This action cannot be easily undone.`,
      header: 'Archive Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        archiveMutation.mutate(undefined, {
          onSuccess: () => {
            toast?.current?.show({
              severity: 'success',
              summary: 'Document archived',
              detail: 'The document has been successfully archived.',
            })
          },
        })
      },
    })
  }

  return (
    <div className='p-4 border rounded-lg border-red-200 bg-red-50 flex flex-col gap-3 mt-4'>
      <h3 className='font-bold text-red-700 uppercase text-xs tracking-wider'>Danger Zone</h3>
      <p className='text-xs text-red-600'>
        Archiving this document will remove it from active lists and move it to the archive.
      </p>
      <Button
        label='Archive Document'
        icon='pi pi-trash'
        severity='danger'
        className='p-button-sm'
        onClick={confirmArchive}
        loading={archiveMutation.isLoading}
      />
    </div>
  )
}

const DocumentDetailsDrawer = ({ documentId, onHide, onBack }: DocumentDetailsDrawerProps) => {
  return (
    <DrawerWrapper
      visible={documentId !== null}
      onHide={onHide}
      position='right'
      className='w-full max-w-160'
      blockScroll
    >
      {documentId && (
        <DocumentDetailsDrawerContent documentId={documentId} onBack={onBack} onHide={onHide} />
      )}
    </DrawerWrapper>
  )
}

export default DocumentDetailsDrawer
